import { CapacitorHttp } from '@capacitor/core';

import { appendAppLog } from '../platform/appLog';
import { istGeraet } from '../platform/gleis';
import { splitMultipart } from './multipart';
import { decryptStream, fromBase64, openEnvelope, PreviewError, type Bytes, type PreviewHeader } from './previewCrypto';
import { importPreviewKeys, PREVIEW_RECIPIENT, type PreviewKeys } from './previewKey';

/**
 * Abholen und quittieren bei Message — übernommen von Ink (`INK/src/preview/previewHttp.ts`,
 * `3b8992e`), Empfänger `Stage-Preview`.
 */

/** Was ein Preview-QR, eine Web-Adresse oder ein Aufrufparameter nennt. */
export interface PreviewCode {
  url: string;
  id: string;
}

export interface PreviewMessage {
  header: PreviewHeader;
  /** Klartext je Datei-`id` aus `header.files`. */
  files: Map<string, Bytes>;
}

export interface PreviewResponse {
  status: number;
  contentType: string;
  /** Der `Date`-Kopf der Antwort, falls da — er unterscheidet eine falsche Uhr von einer falschen Signatur. */
  date: string | null;
  body: Bytes;
}

/** Einsetzbar für Tests; ohne Angabe das Gerät, die Uhr und der feste Schlüssel. */
export interface PreviewIo {
  http?: PreviewHttp;
  now?: () => number;
  keys?: PreviewKeys;
}

export type PreviewHttp = (req: {
  method: 'GET' | 'DELETE';
  url: string;
  headers: Record<string, string>;
}) => Promise<PreviewResponse>;

/**
 * Größer kommt keine Preview herein: alles liegt beim Entschlüsseln im Speicher.
 * shortcut: ganze Nachricht im Speicher, auf Ströme umbauen, wenn Relay größere Previews braucht.
 */
export const MAX_PREVIEW_BYTES = 200 * 1024 * 1024;

/** Vertrag Message: Zeitfenster ±300 s. */
const CLOCK_WINDOW_S = 300;

const base64url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const base64 = (buf: ArrayBuffer): string => {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
};

/** Signierter Text nach Vertrag: `METHODE\nPFAD\nrecipient\nzeit\nnonce`, ohne Schluss-`\n`. */
export async function signedHeaders(
  method: 'GET' | 'DELETE',
  path: string,
  sign: CryptoKey,
  now: number = Date.now(),
  nonce: string = base64url(crypto.getRandomValues(new Uint8Array(16))),
): Promise<Record<string, string>> {
  const ts = String(Math.floor(now / 1000));
  const text = `${method}\n${path}\n${PREVIEW_RECIPIENT}\n${ts}\n${nonce}`;
  const sig = await crypto.subtle.sign({ name: 'RSA-PSS', saltLength: 32 }, sign, new TextEncoder().encode(text));
  return { 'X-Timestamp': ts, 'X-Nonce': nonce, 'X-Signature': base64(sig) };
}

/*
 * Am Gerät der native Weg, aus demselben Grund wie in `server/client.ts::nativerTransport`:
 * aus dem WKWebView ist jeder Aufruf fremdherkünftig. `responseType: 'arraybuffer'` liefert den
 * binären Rumpf als Base64 (so hat Ink es gewählt). Im Browser und am Desktop der gewöhnliche
 * `fetch` mit CORS bei Message.
 */
const deviceHttp: PreviewHttp = async ({ method, url, headers }) => {
  const r = await CapacitorHttp.request({ method, url, headers, responseType: 'arraybuffer' });
  const h = Object.fromEntries(Object.entries(r.headers ?? {}).map(([k, v]) => [k.toLowerCase(), String(v)]));
  const body =
    typeof r.data === 'string' ? fromBase64(r.data) : new TextEncoder().encode(r.data ? JSON.stringify(r.data) : '');
  return { status: r.status, contentType: h['content-type'] ?? '', date: h['date'] ?? null, body };
};

const webHttp: PreviewHttp = async ({ method, url, headers }) => {
  const r = await fetch(url, { method, headers });
  return {
    status: r.status,
    contentType: r.headers.get('content-type') ?? '',
    date: r.headers.get('date'),
    body: new Uint8Array(await r.arrayBuffer()),
  };
};

const defaultHttp = (): PreviewHttp => (istGeraet() ? deviceHttp : webHttp);

const pathOf = (id: string) => `/api/v1/messages/${id.toLowerCase()}`;
const urlOf = (code: PreviewCode) =>
  `${code.url.replace(/\/+$/, '')}${pathOf(code.id)}?recipient=${encodeURIComponent(PREVIEW_RECIPIENT)}`;

async function call(
  method: 'GET' | 'DELETE',
  code: PreviewCode,
  http: PreviewHttp,
  now: () => number,
  keys: PreviewKeys,
): Promise<PreviewResponse> {
  const headers = await signedHeaders(method, pathOf(code.id), keys.sign, now());
  try {
    return await http({ method, url: urlOf(code), headers });
  } catch (e) {
    throw new PreviewError('network', e instanceof Error ? e.message : String(e));
  }
}

const failure = (r: PreviewResponse, now: number): PreviewError => {
  if (r.status === 404) return new PreviewError('notFound');
  if (r.status === 401) {
    const server = r.date ? Date.parse(r.date) : NaN;
    const off = Number.isNaN(server) ? 0 : Math.abs(server - now) / 1000;
    return new PreviewError(off > CLOCK_WINDOW_S ? 'clock' : 'signature');
  }
  return new PreviewError('network', `HTTP ${r.status}`);
};

/** Holt und entschlüsselt die ganze Nachricht. Löscht nichts — das tut erst `confirmPreview`. */
export async function fetchPreview(code: PreviewCode, io: PreviewIo = {}): Promise<PreviewMessage> {
  const now = io.now ?? Date.now;
  const keys = io.keys ?? (await importPreviewKeys());
  const r = await call('GET', code, io.http ?? defaultHttp(), now, keys);
  if (r.status !== 200) throw failure(r, now());
  if (r.body.length > MAX_PREVIEW_BYTES) throw new PreviewError('tooLarge', String(r.body.length));

  const parts = splitMultipart(r.contentType, r.body);
  const envelopePart = parts.find((p) => p.name === 'envelope');
  if (!envelopePart) throw new PreviewError('format', 'kein Umschlag');
  let envelope: { version: number; key: string; header: PreviewHeader };
  try {
    envelope = JSON.parse(new TextDecoder().decode(envelopePart.body));
  } catch {
    throw new PreviewError('format', 'Umschlag');
  }
  const { aes, header } = await openEnvelope(keys, envelope);

  const files = new Map<string, Bytes>();
  for (const f of header.files) {
    const part = parts.find((p) => p.name === `file-${f.id}`);
    if (!part) throw new PreviewError('format', `Datei ${f.id} fehlt`);
    files.set(f.id, await decryptStream(aes, part.body));
  }
  return { header, files };
}

/** Die Quittung: erst danach löscht Message. Scheitert sie, räumt dort die TTL auf. */
export async function confirmPreview(code: PreviewCode, io: PreviewIo = {}): Promise<void> {
  try {
    const keys = io.keys ?? (await importPreviewKeys());
    const r = await call('DELETE', code, io.http ?? defaultHttp(), io.now ?? Date.now, keys);
    if (r.status !== 204 && r.status !== 404) await appendAppLog(`Preview: Quittung abgewiesen, HTTP ${r.status}`, 'warn');
  } catch (e) {
    await appendAppLog(`Preview: Quittung gescheitert — ${e instanceof Error ? e.message : String(e)}`, 'warn');
  }
}
